import * as d3 from 'd3';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';
import Chart from '../Chart/Chart';

const axes = {
    labelX: 'Dollars',
    labelY: '% likelihood',
    prefixY: '',
    suffixY: '%',
};
const barGap = 5;

export default function PotValueChart() {
    const [chartRect, setChartRect] = useState<DOMRect>(new DOMRect());
    const [container, setContainer] = useState(d3.select(null));

    const containerRef = useCallback((ref: any) => {
        const newContainer = d3.select(ref);
        setContainer(newContainer);
    }, []);

    const bars = useMemo(() => {
        const { bins, max, size } = setup.charts.potValue;
        return bins.map((count, index) => ({
            max: (index + 1) * size,
            min: index * size,
            value: count / max,
        }));
    }, []);

    const scaleX = useMemo(() => {
        const maxX = Math.max(...bars.map(bar => bar.max));
        return d3
            .scaleLinear()
            .domain([0, maxX * 1.2])
            .range([0, chartRect.width]);
    }, [bars, chartRect]);

    const scaleY = useMemo(() => {
        const maxY = Math.max(...bars.map(bar => bar.value));
        return d3
            .scaleLinear()
            .domain([0, maxY * 1.2])
            .range([chartRect.height, 0]);
    }, [bars, chartRect]);

    useEffect(() => {
        // Empty container
        container.selectChildren().remove();

        // Check bars
        if (!bars.length || !chartRect.width) {
            return;
        }

        // Get data
        const data = bars.map(bar => ({
            ...bar,
            height: chartRect.height - scaleY(bar.value),
            width: Math.max(0, scaleX(bar.max - bar.min) - barGap),
            x: scaleX(bar.min) + barGap,
            y: scaleY(bar.value),
        }));

        // Add bars
        container
            .selectAll('rect')
            .data(data)
            .enter()
            .append('rect')
            .attr('x', d => d.x)
            .attr('y', scaleY.range()[0])
            .attr('width', d => d.width)
            .attr('height', 0)
            .attr('data-tooltip-id', 'chart-tooltip')
            .attr(
                'data-tooltip-html',
                d => `<b>Dollars:</b> ${d.min} - ${d.max}<br/><b>Likelihood:</b> ${d.value.toFixed(2)}%`
            )
            .style('fill', setup.colors.primary)
            .transition()
            .duration(1000)
            .attr('y', d => d.y)
            .attr('height', d => d.height);
    }, [bars, chartRect, container, scaleX, scaleY]);

    return (
        <Chart axes={axes} scaleX={scaleX} scaleY={scaleY} title="Value of the pot" onRectChange={setChartRect}>
            <g ref={containerRef} />
        </Chart>
    );
}
